import React, { useState, useEffect, useCallback } from 'react';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import AdminPage from './pages/AdminPage';
import SettingsPage from './pages/SettingsPage';
import HistoryPage from './pages/HistoryPage';
import EmailPage from './pages/EmailPage';
import AutomationPage from './pages/AutomationPage';
import ActivityPage from './pages/ActivityPage';
import { api, getToken, setToken, removeToken } from './api';

const SESSION_CHECK_INTERVAL = 60 * 1000; // Check every 1 minute

function MaintenancePage({ theme, setTheme }) {
  return (
    <div className={`login-container ${theme}`}>
      <div className="bg-glow" />
      <button className="theme-toggle" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
        {theme === 'dark' ? '\u2600' : '\u263E'}
      </button>
      <div className="login-card" style={{ textAlign: 'center', padding: '48px 32px' }}>
        <div className="card-line" />
        <div style={{
          width: 64, height: 64, borderRadius: 16,
          background: 'rgba(245,158,11,0.1)', color: '#f59e0b',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 24px', fontSize: 32
        }}>
          &#9888;
        </div>
        <h2 style={{ color: 'var(--title-color)', fontSize: '1.25rem', fontWeight: 600, marginBottom: 8 }}>
          Sistema em Manuten\u00e7\u00e3o
        </h2>
        <p style={{ color: 'var(--subtitle-color)', fontSize: '0.85rem', lineHeight: 1.6, marginBottom: 24 }}>
          Estamos realizando melhorias na plataforma.<br />
          Por favor, tente novamente em alguns instantes.
        </p>
        <div style={{
          padding: '12px 18px', borderRadius: 10,
          background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)',
          color: '#f59e0b', fontSize: '0.75rem'
        }}>
          Previs\u00e3o: em breve
        </div>
      </div>
      <div className="login-footer">Solvere &copy; {new Date().getFullYear()}</div>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [theme, setTheme] = useState('dark');
  const [page, setPage] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [maintenance, setMaintenance] = useState(false);

  const handleLogout = useCallback(async () => {
    try { await api.logout(); } catch {}
    removeToken();
    setUser(null);
    setPage('dashboard');
  }, []);

  // Session validation on load + maintenance check
  useEffect(() => {
    const init = async () => {
      try {
        const { maintenance: m } = await api.checkMaintenance();
        setMaintenance(m);
      } catch {}

      const token = getToken();
      if (token) {
        try {
          const u = await api.me();
          setUser(u);
        } catch {
          removeToken();
        }
      }
      setLoading(false);
    };
    init();
  }, []);

  // Periodic session check - auto-logout if session expired
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(async () => {
      try {
        await api.me();
      } catch (err) {
        if (err.status === 401) {
          removeToken();
          setUser(null);
          setPage('dashboard');
        }
      }
    }, SESSION_CHECK_INTERVAL);

    return () => clearInterval(interval);
  }, [user]);

  const handleLogin = async (cpf, password, totpCode, deviceCode) => {
    const data = await api.login(cpf, password, totpCode, deviceCode);

    // If 2FA or device verification required, signal back
    if (data.requires2FA || data.requiresDeviceVerification) {
      return data;
    }

    setToken(data.token);
    setUser(data.user);
    setPage('dashboard');
    return data;
  };

  if (loading) return null;

  // Show maintenance page for non-logged users or non-admin users
  if (maintenance && (!user || user.role !== 'admin')) {
    return <MaintenancePage theme={theme} setTheme={setTheme} />;
  }

  if (!user) {
    return <LoginPage onLogin={handleLogin} theme={theme} setTheme={setTheme} />;
  }

  if (page === 'admin' && (user.role === 'admin' || user.role === 'supervisor')) {
    return (
      <AdminPage
        user={user}
        onLogout={handleLogout}
        theme={theme}
        setTheme={setTheme}
        setPage={setPage}
      />
    );
  }

  if (page === 'emails' && (user.role === 'admin' || user.role === 'supervisor')) {
    return (
      <EmailPage
        user={user}
        onLogout={handleLogout}
        theme={theme}
        setTheme={setTheme}
        setPage={setPage}
      />
    );
  }

  if (page === 'activity' && (user.role === 'admin' || user.role === 'supervisor')) {
    return (
      <ActivityPage
        user={user}
        onLogout={handleLogout}
        theme={theme}
        setTheme={setTheme}
        setPage={setPage}
      />
    );
  }

  if (page === 'automation' && (user.role === 'admin' || user.role === 'supervisor')) {
    return (
      <AutomationPage
        user={user}
        onLogout={handleLogout}
        theme={theme}
        setTheme={setTheme}
        setPage={setPage}
      />
    );
  }

  if (page === 'settings') {
    return (
      <SettingsPage
        user={user}
        setUser={setUser}
        onLogout={handleLogout}
        theme={theme}
        setTheme={setTheme}
        setPage={setPage}
      />
    );
  }

  if (page === 'history') {
    return (
      <HistoryPage
        user={user}
        onLogout={handleLogout}
        theme={theme}
        setTheme={setTheme}
        setPage={setPage}
      />
    );
  }

  return (
    <DashboardPage
      user={user}
      setUser={setUser}
      onLogout={handleLogout}
      theme={theme}
      setTheme={setTheme}
      setPage={setPage}
    />
  );
}

export default App;
